/* The landing's behaviour: the star field, the glass mark's arrival, the gateway (the Discord sign-in as a
   place in the same space), the handoff toward Discord. Everything here enhances an HTML page that already
   reads and works without it: links go to /login and /auth/login, the gateway is visible on /login. */
import './landing.css';
import { createSpace, type Space } from './space';
import { createStory, type PublicLanding } from './scroll';
import type { Mark } from './mark';

const html = document.documentElement;
const q = new URLSearchParams(location.search);
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const poster = q.get('poster');   // dev and build scripts: render the hero's rest frame alone
const $ = <T extends Element = HTMLElement>(s: string) => document.querySelector<T>(s);

/* ---------- language: the current pill; the links reload in the other language (the server keeps it) ---------- */
document.querySelectorAll<HTMLAnchorElement>('.ld-lang a').forEach(a => a.setAttribute('aria-current', String(a.dataset.lang === html.lang)));

/* ---------- space ---------- */
const space: Space | null = (() => {
  try { return createSpace($<HTMLCanvasElement>('.ld-stars')!, { reduced }); } catch { return null; }
})();
if (space) html.classList.add('ld-webgl');

let lastY = scrollY;
addEventListener('scroll', () => {
  const y = scrollY;
  space?.setScroll(y);
  // the top line steps aside on the way down after the hero, comes back on the way up
  html.classList.toggle('ld-top-hidden', y > innerHeight * 0.6 && y > lastY);
  lastY = y;
}, { passive: true });

/* ---------- the scroll ---------- */
const decks = JSON.parse($('#ld-copy')?.textContent ?? '{}') as Record<string, Record<string, string>>;
const story = poster ? null : createStory({ space, reduced, lang: html.lang, words: decks[html.lang] ?? {} });
if (story) {
  void fetch(`${location.origin}/api/public/landing`, { headers: { accept: 'application/json' } })
    .then(r => (r.ok ? (r.json() as Promise<PublicLanding>) : null)).then(d => story.setData(d)).catch(() => story.setData(null));
}

/* ---------- the arrival ---------- */
const route = html.dataset.route ?? 'landing';
let firstVisit = true;
try { firstVisit = !sessionStorage.getItem('prismArrived'); sessionStorage.setItem('prismArrived', '1'); } catch { /* not remembered */ }
const lowPower = ((navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 8) <= 4 && (navigator.hardwareConcurrency ?? 8) <= 4;
const saveData = !!(navigator as unknown as { connection?: { saveData?: boolean } }).connection?.saveData;
// phones and touch screens keep the poster (the same frame, rendered by the same scene): the 3D chunk and its
// flight would compete with the first paint on a mobile network (LANDING_SPEC.md 5, the power threshold)
const desktop = matchMedia('(pointer: fine) and (min-width: 768px)').matches;
const want3d = !!space && !reduced && !lowPower && !saveData && desktop;
const flight = want3d && firstVisit && route === 'landing' && !poster;

const reveal = (at: number[]) => {
  const [w, t, c] = at;
  setTimeout(() => html.classList.add('ld-show-word'), w);
  setTimeout(() => html.classList.add('ld-show-tag'), t);
  setTimeout(() => { html.classList.add('ld-show-cta'); html.classList.remove('ld-arriving'); }, c);
};
if (flight) html.classList.add('ld-arriving');

let mark: Mark | null = null;
const markCanvas = $<HTMLCanvasElement>('.ld-mark')!;

let loading: Promise<Mark | null> | null = null;
function load3d(): Promise<Mark | null> {
  if (!want3d && !poster) return Promise.resolve(null);
  loading ??= import('./mark').then(({ createMark }) => {
    const m = createMark(markCanvas, { reduced, poster: !!poster });
    mark = m;
    story?.setMark(m);
    return m;
  }).catch(() => null);
  return loading;
}

if (poster) {
  // the poster: the mark at rest, nothing else, on a transparent ground
  html.classList.add('ld-poster-mode');
  load3d().then(m => { m?.render(); requestAnimationFrame(() => { (window as unknown as { __posterReady: boolean }).__posterReady = true; }); });
} else if (flight) {
  // the flight starts as soon as the 3D is ready; if it is not there in 600 ms the poster simply stays
  const fallback = setTimeout(() => { html.classList.remove('ld-arriving'); reveal([0, 120, 240]); }, 600);
  load3d().then(m => {
    if (!m || !html.classList.contains('ld-arriving')) { if (m) { m.arrive(true); html.classList.add('ld-live'); } return; }
    clearTimeout(fallback);
    html.classList.add('ld-live');
    reveal([1500, 1800, 2100]);
    void m.arrive(false);
  });
} else {
  // a repeat visit, the gateway, reduced motion or no WebGL: everything at rest at once
  html.classList.add('ld-show-word', 'ld-show-tag', 'ld-show-cta');
  const go = () => load3d().then(m => { if (m) { m.arrive(true); requestAnimationFrame(() => html.classList.add('ld-live')); } });
  if (want3d) ('requestIdleCallback' in window ? requestIdleCallback(() => void go(), { timeout: 1200 }) : setTimeout(() => void go(), 300));
}

/* skip the flight on any intent */
const skip = () => {
  if (!html.classList.contains('ld-arriving')) return;
  html.classList.remove('ld-arriving');
  html.classList.add('ld-show-word', 'ld-show-tag', 'ld-show-cta');
  mark?.arrive(true);
};
addEventListener('keydown', skip, { once: true });
addEventListener('wheel', skip, { once: true, passive: true });
addEventListener('pointerdown', skip, { once: true });

/* the pointer tilts the mark a few degrees; a slow pass of light every 8 s at rest */
addEventListener('pointermove', e => {
  if (e.pointerType === 'mouse') mark?.setPointer((e.clientX / innerWidth) * 2 - 1, (e.clientY / innerHeight) * 2 - 1);
}, { passive: true });
if (!reduced) setInterval(() => { if (!document.hidden && !html.classList.contains('ld-gw-on') && scrollY < innerHeight) mark?.sweep(); }, 8000);

/* signed out: a quiet line under the tagline, then gone */
if (q.get('signed_out') === '1') {
  const f = $('.ld-flash')!;
  f.textContent = decks[html.lang]?.['out.signed'] ?? 'Signed out.';
  f.classList.add('on');
  setTimeout(() => f.classList.remove('on'), 4000);
  history.replaceState(null, '', location.pathname);
}

/* ---------- the gateway ---------- */
const gw = $('#gateway')!;
const panel = $('.ld-gw-panel')!;
/** the page behind the dialog: out of reach (focus, clicks, assistive tech) while the gateway is open */
const behind = [...document.querySelectorAll<HTMLElement>('body > :not(#gateway):not(script):not(canvas):not(.ld-stars-css)')];
let opener: HTMLElement | null = null;
let fromY = 0;
let pushed = false;
let anim = 0;

/** animates the camera's approach (0 → 1) or retreat; interruptible */
function tweenGate(to: number, ms: number): Promise<void> {
  cancelAnimationFrame(anim);
  const from = gateT, t0 = performance.now();
  return new Promise(res => {
    if (reduced || !mark) { gateT = to; mark?.setGateway(to); space?.setDim(to); res(); return; }
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 3);
      gateT = from + (to - from) * e;
      mark!.setGateway(gateT); space?.setDim(gateT);
      if (k < 1) anim = requestAnimationFrame(step); else res();
    };
    anim = requestAnimationFrame(step);
  });
}
let gateT = 0;

function focusables(): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')].filter(el => el.offsetParent !== null && el.getAttribute('aria-disabled') !== 'true');
}

async function openGateway(from: HTMLElement | null, push: boolean) {
  if (html.classList.contains('ld-gw-on')) return;
  opener = from;
  fromY = scrollY;
  if (push && location.pathname !== '/login') { history.pushState({ gateway: true }, '', '/login' + location.search); pushed = true; }
  skip();
  html.classList.add('ld-gw-on');
  gw.removeAttribute('aria-hidden');
  behind.forEach(el => { el.inert = true; });
  mark?.setGateBox(panel.offsetWidth, panel.offsetHeight);
  await tweenGate(1, 900);
  html.classList.add('ld-gw-ready');
  ($('#gw-title') as HTMLElement).setAttribute('tabindex', '-1');
  ($('#gw-title') as HTMLElement).focus({ preventScroll: true });
}

async function closeGateway() {
  if (!html.classList.contains('ld-gw-on') || html.classList.contains('ld-gw-leaving')) return;
  html.classList.remove('ld-gw-ready');
  await tweenGate(0, 700);
  html.classList.remove('ld-gw-on');
  behind.forEach(el => { el.inert = false; });
  html.dataset.route = 'landing';
  html.dataset.authError = '';
  scrollTo({ top: fromY });
  (opener ?? $('.ld-hero .ld-enter'))?.focus({ preventScroll: true });
}

function back() {
  if (pushed) { pushed = false; history.back(); return; }   // popstate closes it
  history.replaceState(null, '', '/');
  void closeGateway();
}

document.addEventListener('click', e => {
  const t = (e.target as Element).closest<HTMLElement>('[data-gateway-open], [data-gateway-close], [data-gateway-go]');
  if (!t || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
  if (t.matches('[data-gateway-open]')) { e.preventDefault(); void openGateway(t, true); }
  else if (t.matches('[data-gateway-close]')) { e.preventDefault(); back(); }
  else if (t.matches('[data-gateway-go]')) { e.preventDefault(); leave((t as HTMLAnchorElement).href); }
});
addEventListener('popstate', () => {
  if (location.pathname === '/login') void openGateway(null, false);
  else { pushed = false; void closeGateway(); }
});
addEventListener('keydown', e => {
  if (!html.classList.contains('ld-gw-on')) return;
  if (e.key === 'Escape') { e.preventDefault(); back(); return; }
  if (e.key !== 'Tab') return;
  // focus stays inside the dialog
  const f = focusables();
  if (!f.length) return;
  const first = f[0]!, last = f[f.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
});

/** leaving for Discord: light runs once around the panel, the scene settles on the handoff frame, then
    the real redirect. PRISM's Ignition starts from that same frame after the sign-in. */
function leave(href: string) {
  html.classList.add('ld-gw-leaving');
  const go = () => { location.href = href; };
  if (reduced || !mark) { setTimeout(go, reduced ? 150 : 640); return; }
  setTimeout(() => {
    html.classList.add('ld-handoff');
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 500);
      mark!.setHandoff(1 - Math.pow(1 - k, 3));
      if (k < 1) requestAnimationFrame(step); else go();
    };
    requestAnimationFrame(step);
  }, 640);
}

/* a rate-limited sign-in: the action waits out the window the server named */
const retry = Number(q.get('retry') ?? 0);
if (html.dataset.authError === 'rate' && retry > 0) {
  const cta = $('.ld-gw-cta')!;
  cta.setAttribute('aria-disabled', 'true');
  setTimeout(() => cta.removeAttribute('aria-disabled'), retry * 1000);
}

/* /login (or a failed sign-in) opens straight into the gateway */
if (route === 'login') {
  html.dataset.route = 'landing';   // the JS gateway takes over from the no-JS layout
  const open = () => openGateway(null, false);
  if (want3d) load3d().then(m => { m?.arrive(true); html.classList.add('ld-live'); void open(); });
  else void open();
}
